import { NEAR_CAP, STATUS_BADGE, URGENCY_GLYPH, urgencyOf } from './deco';
import type { Status, Tone, Urgency } from './deco';
import { cd, fD, money, pct, short, spoken, SYMBOL } from './format';
import { fullMoney, stated } from './raise-figures';
import { monogram } from '../../shared/feed/format';

/**
 * The landing page's live raises: the hero's "Open right now" cards, the
 * Weekly Sonar card's tiles and the rule builder's matches. Real raises from
 * the feed (HomeRaisesService), never the sample deals in data.ts, which
 * only the labelled sample alerts read. Pure, so the server render, the
 * ticking browser and the tests agree.
 *
 * Every raise here has a close date, and a raise counts as live only while
 * that date is still ahead (liveAt): the server reads the feed every ten
 * minutes, but filters by the clock at every render.
 */

/** A live raise as the landing page reads it. Instants are epoch ms. */
export interface HomeRaise {
  id: string;
  /** Its public slug: /raises/{slug}. */
  slug: string;
  name: string;
  /** The platform chip: the portal's name, else the source's ("SEC EDGAR"). */
  platform: string;
  /** "Reg CF"; null where the source did not say. */
  regime: string | null;
  currency: string | null;
  raised: number | null;
  target: number | null;
  max: number | null;
  launchedAt: number | null;
  closesAt: number;
  /** The region a rule files it under: "EU", "UK", "US", … or "Other". */
  region: string;
  /** Which of the rule builder's sectors (RULE_SECTORS) a rule files it under. */
  sectors: string[];
  /** Its minimum ticket is at most the rule builder's €500, or not stated. */
  ticketOk: boolean;
}

export const DAY = 86_400_000;
export const WEEK = 7 * DAY;

/** How many cards the hero shows. */
export const HERO_COUNT = 3;

/** Launched, and closing after `now`. */
export function liveAt(raises: readonly HomeRaise[], now: number): HomeRaise[] {
  return raises.filter(
    (r) => r.closesAt > now && (r.launchedAt === null || r.launchedAt <= now),
  );
}

/** Close first, then name, then id: a total order. */
export function byClose(a: HomeRaise, b: HomeRaise): number {
  return (
    a.closesAt - b.closesAt ||
    a.name.localeCompare(b.name) ||
    (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  );
}

/** Raised over the maximum, or null without both. */
export function shareOfMax(r: HomeRaise): number | null {
  return r.raised !== null && stated(r.max) ? r.raised / r.max : null;
}

/** At 90% of its maximum or past it, as the feed's Near cap is. */
export function isNearCap(r: HomeRaise): boolean {
  const share = shareOfMax(r);
  return share !== null && share >= NEAR_CAP;
}

/** What the bar measures against: the maximum, else the target. */
function scaleOf(r: HomeRaise): number | null {
  if (stated(r.max)) return r.max;
  return stated(r.target) ? r.target : null;
}

/** Shows its card whole: an amount raised, against a maximum or a target. */
function whole(r: HomeRaise): boolean {
  return stated(r.raised) && scaleOf(r) !== null;
}

/**
 * The hero's cards: the near-cap raise closing soonest, then the raises
 * closing soonest, up to three, in the order they close. Raises that show
 * their figures whole go first; one without figures fills a gap only.
 */
export function heroPicks(
  raises: readonly HomeRaise[],
  now: number,
): HomeRaise[] {
  const live = liveAt(raises, now).sort(byClose);
  const picks = live.filter(isNearCap).slice(0, 1);
  const order = [...live.filter(whole), ...live.filter((r) => !whole(r))];
  for (const r of order) {
    if (picks.length >= HERO_COUNT) break;
    if (!picks.includes(r)) picks.push(r);
  }
  return picks.sort(byClose);
}

/** "$904,300", or the ISO code's own form where it has no symbol here. */
function amount(value: number, currency: string | null): string {
  return currency && SYMBOL[currency]
    ? money(value, currency)
    : fullMoney(value, currency);
}

/** "€2.5M", or "1.2M CHF" where the code has no symbol here. */
function shortAmount(value: number, currency: string | null): string {
  if (currency && SYMBOL[currency]) return short(value, currency);
  return `${short(value, '')}${currency ? ` ${currency}` : ''}`;
}

/** What a card's countdown and status are drawn from. */
export interface CardClockInput {
  closesAt: number;
  nearCap: boolean;
}

export interface CardClock {
  statusLabel: string;
  statusGlyph: string;
  statusTone: Tone;
  urgency: Urgency;
  urgencyGlyph: '■' | '▲' | '○';
  countdown: string;
  spoken: string;
  closeShort: string;
}

/**
 * The parts of a card that move with the clock. Near cap wins the status
 * over closing soon, as on the sample cards (deco.ts); a raise whose close
 * passes while the page is open says Closed.
 */
export function cardClock(c: CardClockInput, now: number): CardClock {
  const msLeft = c.closesAt - now;
  const urgency = urgencyOf(msLeft);
  const status: Status =
    msLeft <= 0
      ? 'closed'
      : c.nearCap
        ? 'nearcap'
        : urgency !== 'calm'
          ? 'closing'
          : 'live';
  const badge = STATUS_BADGE[status](urgency);
  return {
    statusLabel: badge.label,
    statusGlyph: badge.glyph,
    statusTone: badge.tone,
    urgency,
    urgencyGlyph: URGENCY_GLYPH[urgency],
    countdown: cd(msLeft),
    spoken: spoken(msLeft),
    closeShort: fD(c.closesAt),
  };
}

/** A raise's page on this site. */
export function raisePath(slug: string): string {
  return `/raises/${encodeURIComponent(slug)}`;
}

/** One hero card, as views/partials/site-deal-card.hbs draws it. */
export function liveCard(r: HomeRaise, now: number) {
  const scale = scaleOf(r);
  const ratio = r.raised !== null && scale !== null ? r.raised / scale : null;
  const fill = ratio === null ? 0 : Math.min(100, ratio * 100);
  const nearCap = isNearCap(r);
  return {
    id: r.id,
    href: raisePath(r.slug),
    name: r.name,
    platform: r.platform,
    mono: monogram(r.platform),
    regime: r.regime,
    raised: r.raised === null ? null : amount(r.raised, r.currency),
    pctLabel:
      ratio === null || scale === null
        ? null
        : `${pct(ratio * 100)} of ${shortAmount(scale, r.currency)}`,
    fill,
    fillStep: fill < 34 ? 'low' : fill < 67 ? 'mid' : 'high',
    targetAt:
      stated(r.max) && stated(r.target)
        ? Math.min(100, (r.target / r.max) * 100)
        : null,
    // For the script, which keeps the clock's parts current.
    closesAt: r.closesAt,
    nearCap,
    ...cardClock({ closesAt: r.closesAt, nearCap }, now),
  };
}
